import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useLoader } from "@/hooks/LoaderProvider";
import {
  updateStep,
  setCurrentStep,
  selectStep1Data,
  selectStep2Data,
  selectStep3Data,
  updateOrganization,
  selectIsOrganizationCreated,
  createOrganization,
} from "@/store/redux/slices/kyc/kyc_setup_slice";
import {
  uploadFile,
  selectFileUploadLoading,
} from "@/store/redux/slices/user/file_upload_slice";
import { getUserId, getOrganizationId } from "@/utils/token_functions";
import Button from "@/components/common/Button";
import Input from "@/components/common/inputs/Input";
import FileInput from "@/components/common/inputs/File_Input";
import Success from "@/components/common/alerts/Success";
import { ArrowRightOutlined } from "@/config/antd";
import Copyright from "@/components/layout/Footer/Copyright";

const AccountSetupStep3 = () => {
  const { showLoader, hideLoader } = useLoader();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Get data from all steps
  const step1Data = useSelector(selectStep1Data);
  const step2Data = useSelector(selectStep2Data);
  const step3Data = useSelector(selectStep3Data);
  const isFileUploading = useSelector(selectFileUploadLoading);
  const isOrganizationCreated = useSelector(selectIsOrganizationCreated);

  const [isSuccessVisible, setIsSuccessVisible] = useState(false);
  const [isSecondSuccessVisible, setIsSecondSuccessVisible] = useState(false);
  const [cacRegistrationNumber, setCacRegistrationNumber] = useState(
    step3Data?.CACRegistrationCertificateNumber || ""
  );
  const [cacCertificate, setCacCertificate] = useState(
    step3Data?.CACRegistrationCertificateImage || null
  );
  const [identityType, setIdentityType] = useState(
    step3Data?.modeOfIdentification || ""
  );
  const [identityDocument, setIdentityDocument] = useState(
    step3Data?.identificationImage || null
  );
  const [cacCertificatePreview, setCacCertificatePreview] = useState(null);
  const [identityPreview, setIdentityPreview] = useState(null);

  const isFormFilled = Boolean(
    cacRegistrationNumber && cacCertificate && identityType && identityDocument
  );

  const handleCacCertificateChange = async (file) => {
    if (file) {
      try {
        const reader = new FileReader();
        reader.onload = () => {
          const base64String = reader.result;
          setCacCertificate(file);
          setCacCertificatePreview(base64String);
        };
        reader.readAsDataURL(file);
      } catch (error) {
        console.error("Error processing file:", error);
      }
    }
  };

  const handleIdentityDocumentChange = async (file) => {
    if (file) {
      try {
        const reader = new FileReader();
        reader.onload = () => {
          const base64String = reader.result;
          setIdentityDocument(file);
          setIdentityPreview(base64String);
        };
        reader.readAsDataURL(file);
      } catch (error) {
        console.error("Error processing file:", error);
      }
    }
  };

  const identityTypeOptions = [
    { value: "passport", label: "International Passport" },
    { value: "voter_card", label: "Permanent Voter Card" },
    { value: "electricity_bill", label: "Electricity Bill" },
    { value: "vocational_cert", label: "Recognized Vocational Certificate" },
  ];

  const handleProceed = async () => {
    showLoader();
    try {
      // Store data in Redux
      await dispatch(
        updateStep({
          step: "step3",
          data: {
            CACRegistrationCertificateNumber: cacRegistrationNumber,
            CACRegistrationCertificateImage:
              cacCertificate instanceof File
                ? cacCertificatePreview // Now this is defined
                : cacCertificate,
            modeOfIdentification: identityType,
            identificationImage:
              identityDocument instanceof File
                ? identityPreview // Now this is defined
                : identityDocument,
          },
        })
      );
      setIsSuccessVisible(true);
    } catch (error) {
      console.error("Error saving data:", error);
    } finally {
      hideLoader();
    }
  };

  const handleCloseSuccess = () => {
    setIsSuccessVisible(false);
  };

  const handleFirstSuccessProceed = () => {
    showLoader();
    setTimeout(() => {
      hideLoader();
      dispatch(setCurrentStep(4));
      navigate("/account-setup-review-and-submit");
    }, 2000);
  };

  const handleSaveAndProceedLater = async () => {
    setIsSuccessVisible(false);
    showLoader();

    try {
      const userID = getUserId();
      let filesToUpload = [];

      // Collect all files that need to be uploaded
      if (cacCertificate instanceof File) {
        filesToUpload.push(cacCertificate);
      }

      if (identityDocument instanceof File) {
        filesToUpload.push(identityDocument);
      }

      // Upload all files in a single request if there are any
      let uploadedFiles = [];
      if (filesToUpload.length > 0) {
        const uploadResult = await dispatch(
          uploadFile({
            file: filesToUpload, // The slice will handle appending these to FormData as 'files'
          })
        ).unwrap();

        uploadedFiles = uploadResult;
      }

      // Combine data from all steps
      const organizationData = {
        name: step1Data.name,
        type: step1Data.type,
        address: step1Data.address,
        email: step1Data.email,
        phone: step1Data.phone,
        ownerProfileImage: step1Data.ownerProfileImage,

        ownerFullName: step2Data.ownerFullName,
        ownerEmail: step2Data.ownerEmail,
        ownerPhone: step2Data.ownerPhone,
        isOwner: step2Data.isOwner,
        ownerIdentificationType: step2Data.modeOfIdentification,
        ownerIdentificationImage: step2Data.identificationImage,

        CACRegistrationCertificateNumber: cacRegistrationNumber,
        CACRegistrationCertificateImage: uploadedFiles[0]?.fileUrl || null,
        modeOfIdentification: identityType,
        identificationImage: uploadedFiles[1]?.fileUrl || null,
        userID,
      };

      // Use appropriate action based on organization creation status
      const shouldUpdate =
        isOrganizationCreated || Boolean(getOrganizationId());
      if (!shouldUpdate) {
        await dispatch(createOrganization(organizationData));
      } else {
        await dispatch(updateOrganization(organizationData));
      }

      setIsSecondSuccessVisible(true);
    } catch (error) {
      console.error("Error with organization:", error);
    } finally {
      hideLoader();
    }
  };

  const handleCloseSecondSuccess = () => {
    setIsSecondSuccessVisible(false);
  };

  const handleContinue = () => {
    setIsSecondSuccessVisible(false);
    dispatch(setCurrentStep(4));
    navigate("/account-setup-review-and-submit");
  };

  return (
    <div
      className="flex flex-col min-h-screen text-left bg-white rounded-lg lg:border lg:p-5"
      data-testid="account-setup-step3">
      <h1 className="mb-2 text-3xl font-bold text-left whitespace-nowrap text-primary">
        Business Information
      </h1>
      <p className="mb-4 text-left text-small text-darkGrey">
        Your basic business document&apos;s issued by the government or
        nationally-recognized issuer.
      </p>

      <h2 className="mb-4 text-left text-black heading-medium">
        CAC Registration Certificate
      </h2>
      <p className="mb-4 text-left text-black text-small">
        Please upload your CAC registration number and certificate below.
      </p>

      <div className="flex flex-col w-full mt-4 mb-8 rounded-lg lg:border lg:p-5">
        <div className="mb-4">
          <Input
            label="CAC REGISTRATION NUMBER"
            data-testid="cac-number-input"
            placeholder="Edit"
            type="text"
            value={cacRegistrationNumber}
            onChange={(e) => setCacRegistrationNumber(e.target.value)}
          />
        </div>

        <div className="mb-4">
          <FileInput
            id="cacCertificate"
            label="UPLOAD FILE"
            placeholder={cacCertificate ? "File uploaded" : "File Name.png"}
            onChange={handleCacCertificateChange}
            accept=".pdf,.jpg,.jpeg,.png"
            isLoading={isFileUploading}
          />
        </div>

        <h2 className="mt-6 mb-4 text-left text-black heading-medium">
          Government-Issued ID
        </h2>
        <p className="mb-4 text-left text-small text-darkGrey">
          Any government-issued for operation.
        </p>

        <div className="mb-4">
          <Input
            label="MODE OF IDENTITY"
            data-testid="identity-type-input"
            placeholder="Select"
            type="select"
            options={identityTypeOptions}
            value={identityType}
            onChange={(e) => setIdentityType(e.target.value)}
          />
        </div>

        <div className="mb-4">
          <FileInput
            id="identityDocument"
            label="UPLOAD FILE"
            placeholder={identityDocument ? "File uploaded" : "File Name.png"}
            onChange={handleIdentityDocumentChange}
            accept=".pdf,.jpg,.jpeg,.png"
            isLoading={isFileUploading}
          />
        </div>

        <Button
          text="Proceed"
          data-testid="step3-proceed-button"
          icon={<ArrowRightOutlined />}
          onClick={handleProceed}
          isActive={isFormFilled}
          color="darkBlue"
          className={`w-full mt-6 ${
            isFormFilled ? "bg-primary text-white" : "bg-darkBlue text-primary"
          }`}
        />
      </div>

      <div className="mt-auto">
        <Copyright />
      </div>

      <Success
        isVisible={isSuccessVisible}
        onClose={handleCloseSuccess}
        header="KYC Step Saved"
        paragraph="Your kyc step has been saved. You can proceed below or save and continue later."
        buttonText="Proceed"
        showCancelButton={true}
        cancelButtonText="I want to save and proceed later"
        onButtonClick={handleFirstSuccessProceed}
        onCancelClick={handleSaveAndProceedLater}
      />

      <Success
        isVisible={isSecondSuccessVisible}
        onClose={handleCloseSecondSuccess}
        header="KYC Step Saved"
        paragraph="You can proceed with your KYC at any time when you are ready but remember, your account will still be limited until approval."
        buttonText="Continue"
        showCancelButton={false}
        onButtonClick={handleContinue}
      />
    </div>
  );
};

export default AccountSetupStep3;
